import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCRM } from '../../utils/CRMContext';
import { Lead, EngagementLetter, ENGAGEMENT_LETTER_TYPES, DEFAULT_ENGAGEMENT_TERMS, RETAINER_ENGAGEMENT_TERMS, RETAINER_TERMS_CONDITIONS, ClientPortal, PortalStage } from '../../types';
import { FileSignature, Download, Eye, Copy, Send, Trash2, Plus, Lock, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { db, storage } from '../../utils/firebase';
import { collection, query, where, getDocs, addDoc, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getSettings } from '../../utils/appSettings';
import jsPDF from 'jspdf';
import './EngagementLettersList.css';

interface EngagementLettersListProps {
  leadId: string;
  lead?: Lead;
  dealId?: string;
  compact?: boolean;
  defaultExpanded?: boolean;
}

const EngagementLettersList: React.FC<EngagementLettersListProps> = ({
  leadId,
  lead,
  dealId,
  compact = false,
  defaultExpanded = false,
}) => {
  const { engagementLetters, deleteEngagementLetter, updateEngagementLetter, createEngagementLetter, convertLeadToDeal, pipelines } = useCRM();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [creatingEngagement, setCreatingEngagement] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const isCreatingRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [retainerTotalFeeRange, setRetainerTotalFeeRange] = useState('');
  const [engagementLogo, setEngagementLogo] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await getSettings();
      if (settings?.retainerTotalFeeRange) setRetainerTotalFeeRange(settings.retainerTotalFeeRange);
      if (settings?.engagementLogo) setEngagementLogo(settings.engagementLogo);
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (showTypeSelector && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: Math.max(10, rect.left + (rect.width / 2) - 120),
      });
    }
  }, [showTypeSelector]);

  useEffect(() => {
    if (!showTypeSelector) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideButton = buttonRef.current && !buttonRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      if (isOutsideButton && isOutsideDropdown) setShowTypeSelector(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypeSelector]);

  const letters = engagementLetters.filter(l => l.leadId === leadId);

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
      draft:   { label: 'Draft',   color: '#6b7280', bg: '#f3f4f6', icon: <Clock size={12} /> },
      sent:    { label: 'Sent',    color: '#1d4ed8', bg: '#dbeafe', icon: <Send size={12} /> },
      viewed:  { label: 'Viewed',  color: '#7c3aed', bg: '#ede9fe', icon: <Eye size={12} /> },
      signed:  { label: 'Signed',  color: '#d97706', bg: '#fef3c7', icon: <FileSignature size={12} /> },
      paid:    { label: 'Paid',    color: '#059669', bg: '#d1fae5', icon: <CheckCircle2 size={12} /> },
      expired: { label: 'Expired', color: '#dc2626', bg: '#fee2e2', icon: <Clock size={12} /> },
      voided:  { label: 'Voided',  color: '#6b7280', bg: '#e5e7eb', icon: <Clock size={12} /> },
    };
    return configs[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', icon: <Clock size={12} /> };
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCopyLink = (id: string, slug: string, pin: string) => {
    const link = `${window.location.origin}/engagement/${slug}`;
    navigator.clipboard.writeText(`${link}\nPIN: ${pin}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewLetter = (slug: string) => {
    window.open(`/engagement/${slug}`, '_blank');
  };

  const handleResendEmail = async (letter: EngagementLetter) => {
    await updateEngagementLetter(letter.id!, { status: 'sent', sentAt: new Date() });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this engagement letter?')) return;
    setDeletingId(id);
    try {
      await deleteEngagementLetter(id);
    } catch (err) {
      console.error('Error deleting letter:', err);
      alert('Failed to delete engagement letter');
    } finally {
      setDeletingId(null);
    }
  };

  const generateSignedPdf = async (letterData: EngagementLetter): Promise<Blob> => {
    if (!letterData.signature) throw new Error('No signature found');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    const addText = (text: string, fontSize: number, isBold = false, color = '#111827') => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const rgb = parseInt(color.slice(1), 16);
      pdf.setTextColor((rgb >> 16) & 255, (rgb >> 8) & 255, rgb & 255);
      const lines = pdf.splitTextToSize(text, contentWidth);
      pdf.text(lines, margin, yPos);
      yPos += lines.length * (fontSize * 0.4) + 4;
    };

    const checkNewPage = (neededSpace: number) => {
      if (yPos + neededSpace > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yPos = 20;
      }
    };

    let headerHeight = 45;
    pdf.setFillColor(44, 62, 47);

    if (engagementLogo?.url) {
      try {
        headerHeight = 55;
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.src = engagementLogo.url;
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          setTimeout(resolve, 1000);
        });
        const maxLogoHeight = 25;
        const maxLogoWidth = 80;
        let logoWidth = maxLogoWidth;
        let logoHeight = maxLogoHeight;
        if (logoImg.width && logoImg.height) {
          const ratio = logoImg.width / logoImg.height;
          if (ratio > maxLogoWidth / maxLogoHeight) {
            logoWidth = maxLogoWidth;
            logoHeight = maxLogoWidth / ratio;
          } else {
            logoHeight = maxLogoHeight;
            logoWidth = maxLogoHeight * ratio;
          }
        }
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoImg, 'PNG', logoX, 8, logoWidth, logoHeight);
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Engagement Letter', pageWidth / 2, headerHeight - 6, { align: 'center' });
      } catch (e) {
        console.warn('Could not add logo to PDF:', e);
        pdf.rect(0, 0, pageWidth, 45, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text('LC Timber Frames', pageWidth / 2, 18, { align: 'center' });
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Engagement Letter', pageWidth / 2, 32, { align: 'center' });
        headerHeight = 45;
      }
    } else {
      pdf.rect(0, 0, pageWidth, 45, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('LC Timber Frames', pageWidth / 2, 18, { align: 'center' });
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Engagement Letter', pageWidth / 2, 32, { align: 'center' });
    }

    yPos = headerHeight + 10;
    pdf.setTextColor(17, 24, 39);
    addText(letterData.title, 18, true);
    yPos += 5;
    addText(`Prepared for: ${letterData.clientName}`, 12);
    addText(letterData.clientEmail, 10, false, '#6b7280');
    yPos += 8;

    const isRetainer = letterData.letterType === 'retainer' || letterData.designFee === 1600;

    if (isRetainer) {
      checkNewPage(40);
      addText('Introduction', 14, true, '#1a365d');
      addText(`Dear ${letterData.clientName.split(' ')[0]},`, 11);
      addText('At Leiper\'s Creek Timber Frames, we\'re passionate about translating bold ideas into timeless designs through close collaboration with our exceptional clients.', 10);
      yPos += 5;
      checkNewPage(60);
      addText('Scope of Services', 14, true, '#1a365d');
      addText('Deliverables:', 11, true);
      ['1. Interactive 3D Model', '2. 2D Draft Plans'].forEach(d => {
        checkNewPage(10);
        addText(d, 10);
      });
      yPos += 5;
      checkNewPage(60);
      addText('Fee Structure', 14, true, '#1a365d');
      addText('Concept Design Fee: A one-time payment of $1,600 is required upon signing. 2D draft plans are included.', 10);
      yPos += 5;
      checkNewPage(80);
      addText('Terms & Conditions', 14, true, '#1a365d');
      RETAINER_TERMS_CONDITIONS.forEach(term => {
        checkNewPage(15);
        addText(`${term.title}: ${term.content}`, 9);
      });
    } else {
      checkNewPage(40);
      addText('Description of Services', 14, true);
      addText('1. Visually define the timber frame for pricing.', 10);
      addText('2. One revision included at this rate.', 10);
      addText('3. Additional services available at standard rates.', 10);
      yPos += 8;
      checkNewPage(40);
      addText('Deliverables', 14, true);
      letterData.deliverables.forEach(d => {
        checkNewPage(8);
        addText(`• ${d}`, 10);
      });
      yPos += 8;
      checkNewPage(60);
      addText('Terms and Conditions', 14, true);
      letterData.termsAndConditions.split('\n').forEach(para => {
        if (para.trim()) {
          checkNewPage(10);
          addText(para, 9);
        }
      });
    }

    checkNewPage(30);
    yPos += 5;
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, yPos - 5, contentWidth, 25, 'F');
    pdf.setTextColor(22, 101, 52);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Design Fee', margin + 5, yPos + 3);
    pdf.setFontSize(24);
    pdf.text(`$${letterData.designFee.toLocaleString()}`, margin + 5, yPos + 15);
    yPos += 30;

    checkNewPage(60);
    yPos += 10;
    addText('Signature', 14, true);
    try {
      pdf.addImage(letterData.signature.dataUrl, 'PNG', margin, yPos, 60, 25);
      yPos += 30;
    } catch {
      yPos += 5;
    }

    const sigDate = letterData.signature.signedAt as any;
    const signedAt = sigDate?.toDate ? sigDate.toDate() : new Date(sigDate);
    addText(`Signed by: ${letterData.signature.signedBy}`, 10);
    addText(`Date: ${signedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at ${signedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`, 10);

    const footerY = pdf.internal.pageSize.getHeight() - 15;
    pdf.setFontSize(9);
    pdf.setTextColor(107, 114, 128);
    pdf.text('© LC Timber Frames | info@lctimberframes.com', pageWidth / 2, footerY, { align: 'center' });

    return pdf.output('blob');
  };

  const handleDownloadPdf = async (letter: EngagementLetter) => {
    if (!letter.signature) return;
    setGeneratingPdfId(letter.id!);
    try {
      if (letter.signedPdfUrl) {
        window.open(letter.signedPdfUrl, '_blank');
        return;
      }
      const pdfBlob = await generateSignedPdf(letter);
      try {
        const pdfFileName = `engagement-letters/${letter.id}/${letter.slug}-signed.pdf`;
        const storageRef = ref(storage, pdfFileName);
        await uploadBytes(storageRef, pdfBlob);
        const signedPdfUrl = await getDownloadURL(storageRef);
        await updateEngagementLetter(letter.id!, { signedPdfUrl });
        window.open(signedPdfUrl, '_blank');
      } catch (uploadErr) {
        console.warn('Could not upload PDF, downloading locally:', uploadErr);
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${letter.slug}-signed.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF');
    } finally {
      setGeneratingPdfId(null);
    }
  };

  const handleQuickCreate = async (engagementType: typeof ENGAGEMENT_LETTER_TYPES[0]) => {
    if (isCreatingRef.current) return;
    if (!lead?.id) { alert('Lead information not available'); return; }

    isCreatingRef.current = true;
    setCreatingEngagement(true);
    setShowTypeSelector(false);

    try {
      const lastName = lead.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const projectName = (lead.projectType || 'timber-frame').toLowerCase()
        .replace(/[^a-z0-9\s]/g, '').split(' ').slice(0, 2).join('-');
      const engagementSlug = `${lastName}-${projectName}-${engagementType.id}-${Date.now().toString(36)}`;
      const accessPin = Math.floor(1000 + Math.random() * 9000).toString();
      const terms = engagementType.id === 'retainer' ? RETAINER_ENGAGEMENT_TERMS : DEFAULT_ENGAGEMENT_TERMS;

      const engagementLetterId = await createEngagementLetter({
        leadId: lead.id, dealId, slug: engagementSlug, accessPin,
        letterType: engagementType.id,
        title: `${engagementType.name} - ${lead.firstName} ${lead.lastName}`,
        clientName: `${lead.firstName} ${lead.lastName}`,
        clientEmail: lead.email,
        projectDescription: engagementType.description,
        designFee: engagementType.fee,
        deliverables: engagementType.deliverables,
        termsAndConditions: terms,
        status: 'sent',
        sentAt: new Date(),
      });

      let portalSlug: string;
      let portalUrl: string;
      const portalsRef = collection(db, 'clientPortals');
      const existingPortalQuery = query(portalsRef, where('leadId', '==', lead.id));
      const existingPortals = await getDocs(existingPortalQuery);

      let existingPortalDocId: string | null = null;
      if (!existingPortals.empty) {
        const existingPortal = existingPortals.docs[0].data();
        existingPortalDocId = existingPortals.docs[0].id;
        portalSlug = existingPortal.slug;
        portalUrl = `${window.location.origin}/portal/${portalSlug}`;
        // Sync missing fields on existing portal + projects map
        const updates: Record<string, any> = {};
        if (!existingPortal.dealId && dealId) updates.dealId = dealId;
        if (!existingPortal.engagementLetterSlug) updates.engagementLetterSlug = engagementSlug;
        if (!existingPortal.engagementLetterId && engagementLetterId) updates.engagementLetterId = engagementLetterId;
        // Sync engagement letter to project in projects map
        if (dealId) {
          updates[`projects.${dealId}.engagementLetterSlug`] = engagementSlug;
          if (engagementLetterId) updates[`projects.${dealId}.engagementLetterId`] = engagementLetterId;
          updates[`projects.${dealId}.updatedAt`] = Timestamp.now();
          if (!existingPortal.projects?.[dealId]) {
            updates[`projects.${dealId}.dealId`] = dealId;
            updates[`projects.${dealId}.projectName`] = '';
            updates[`projects.${dealId}.currentStage`] = existingPortal.currentStage || 'design-fee';
            updates[`projects.${dealId}.stageHistory`] = existingPortal.stageHistory || [{ stage: 'design-fee', enteredAt: Timestamp.now() }];
            updates[`projects.${dealId}.designFeeSkipped`] = existingPortal.designFeeSkipped || false;
            updates[`projects.${dealId}.createdAt`] = Timestamp.now();
          }
        }
        if (Object.keys(updates).length > 0) {
          updates.updatedAt = Timestamp.now();
          await updateDoc(doc(db, 'clientPortals', existingPortalDocId), updates);
        }
      } else {
        const portalKey = Math.floor(1000 + Math.random() * 9000).toString();
        portalSlug = `${lastName}-${portalKey}`;
        const initialProjects: Record<string, any> = {};
        if (dealId) {
          initialProjects[dealId] = {
            dealId,
            projectName: '',
            engagementLetterId: engagementLetterId || undefined,
            engagementLetterSlug: engagementSlug,
            currentStage: 'design-fee',
            stageHistory: [{ stage: 'design-fee', enteredAt: Timestamp.now() }],
            designFeeSkipped: false,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          };
        }
        const newPortal: Omit<ClientPortal, 'id'> = {
          slug: portalSlug,
          accessPin,
          clientName: `${lead.firstName} ${lead.lastName}`,
          clientEmail: lead.email,
          leadId: lead.id,
          dealId,
          engagementLetterId: engagementLetterId || undefined,
          engagementLetterSlug: engagementSlug,
          currentStage: 'design-fee' as PortalStage,
          stageHistory: [{ stage: 'design-fee' as PortalStage, enteredAt: new Date() }],
          designFeeSkipped: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        await addDoc(portalsRef, {
          ...newPortal,
          ...(Object.keys(initialProjects).length > 0 ? { projects: initialProjects } : {}),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          stageHistory: newPortal.stageHistory.map(h => ({
            ...h, enteredAt: Timestamp.fromDate(h.enteredAt),
          })),
        });
        portalUrl = `${window.location.origin}/portal/${portalSlug}`;
      }

      const engagementUrl = `${window.location.origin}/engagement/${engagementSlug}`;

      // Pipeline auto-sync: auto-create deal and place in "qualified" stage
      if (lead && !lead.convertedToDeal) {
        try {
          const defaultPipeline = pipelines.find(p => p.isDefault) || pipelines[0];
          if (defaultPipeline?.id) {
            const newDealId = await convertLeadToDeal(lead, defaultPipeline.id, 'system');
            // Sync the new dealId back to the portal
            if (newDealId) {
              const portalToUpdate = existingPortalDocId || (await getDocs(query(portalsRef, where('leadId', '==', lead.id)))).docs[0]?.id;
              if (portalToUpdate) {
                await updateDoc(doc(db, 'clientPortals', portalToUpdate), {
                  dealId: newDealId,
                  updatedAt: Timestamp.now(),
                });
              }
            }
          }
        } catch (pipelineErr) {
          console.error('Pipeline auto-sync failed (non-blocking):', pipelineErr);
        }
      }

      navigator.clipboard.writeText(`${portalUrl}\nPIN: ${accessPin}\n\nEngagement Letter: ${engagementUrl}`);
      alert(`Engagement letter & portal created!\n\nPortal: ${portalUrl}\nPIN: ${accessPin}\n\nEngagement Letter: ${engagementUrl}\n\n(Copied to clipboard)`);

    } catch (error) {
      console.error('Error creating engagement letter:', error);
      alert('Error creating engagement letter');
    } finally {
      isCreatingRef.current = false;
      setCreatingEngagement(false);
    }
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  const renderCreateButton = (isAdd = false) => (
    <div className="el-type-selector">
      <button
        ref={buttonRef}
        type="button"
        className={isAdd ? 'el-add-btn' : 'el-create-btn'}
        onClick={(e) => { e.stopPropagation(); setShowTypeSelector(!showTypeSelector); }}
        disabled={creatingEngagement || !lead}
      >
        {creatingEngagement ? (
          <><Loader2 size={14} className="el-spin" /> Creating...</>
        ) : (
          <><Plus size={14} /> {isAdd ? 'Create New Letter' : 'Create Engagement Letter'}</>
        )}
      </button>
      {showTypeSelector && !creatingEngagement && createPortal(
        <div ref={dropdownRef} className="el-type-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {ENGAGEMENT_LETTER_TYPES.map(engType => (
            <button
              key={engType.id}
              type="button"
              className="el-type-option"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleQuickCreate(engType); }}
              disabled={creatingEngagement}
            >
              <div className="el-type-info">
                <span className="el-type-name">{engType.name}</span>
                <span className="el-type-desc">{engType.description}</span>
              </div>
              <span className="el-type-fee">${engType.fee.toLocaleString()}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );

  return (
    <div className={`el-section ${compact ? 'el-compact' : ''}`}>
      {/* Collapsible Header */}
      <div className="el-header" onClick={() => setExpanded(!expanded)}>
        <div className="el-header-left">
          <span className={`el-collapse-arrow ${expanded ? 'expanded' : ''}`} />
          <FileSignature size={15} />
          <span className="el-header-title">Engagement Letters</span>
        </div>
        <div className="el-header-right">
          {letters.length === 0 ? (
            <span className="el-no-items">No letters</span>
          ) : (
            <span className="el-count">{letters.length}</span>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="el-content">
          {letters.length === 0 ? (
            <div className="el-empty">
              <FileSignature size={24} className="el-empty-icon" />
              <p>No engagement letters yet</p>
              {renderCreateButton()}
            </div>
          ) : (
            <>
              <div className="el-list">
                {letters.map(letter => {
                  const sc = getStatusConfig(letter.status);
                  return (
                    <div key={letter.id} className={`el-item ${letter.status}`}>
                      {/* Top row: title + status */}
                      <div className="el-item-top">
                        <div className="el-item-info">
                          <span className="el-item-title">{letter.title}</span>
                          <div className="el-item-meta">
                            <span className="el-item-badge" style={{ color: sc.color, background: sc.bg }}>
                              {sc.icon} {sc.label}
                            </span>
                            <span className="el-item-fee">
                              {letter.status === 'voided'
                                ? 'Voided'
                                : `$${letter.designFee.toLocaleString()}`}
                            </span>
                            <span className="el-item-date">{formatDate(letter.createdAt)}</span>
                          </div>
                        </div>

                        {/* Signature thumbnail */}
                        {letter.signature && (
                          <div className="el-sig-thumb">
                            <img src={letter.signature.dataUrl} alt="Signature" />
                          </div>
                        )}
                      </div>

                      {/* Actions bar */}
                      <div className="el-item-actions">
                        <button className="el-act-btn" onClick={() => handleViewLetter(letter.slug)} title="View Letter">
                          <Eye size={14} /> <span>View</span>
                        </button>
                        <button
                          className="el-act-btn"
                          onClick={() => handleCopyLink(letter.id!, letter.slug, letter.accessPin)}
                          title="Copy Link & PIN"
                        >
                          {copiedId === letter.id ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                          <span>{copiedId === letter.id ? 'Copied' : 'Copy Link'}</span>
                        </button>
                        {(letter.status === 'signed' || letter.status === 'paid') && letter.signature && (
                          <button
                            className={`el-act-btn el-act-pdf ${letter.signedPdfUrl ? 'has-pdf' : ''}`}
                            onClick={() => handleDownloadPdf(letter)}
                            disabled={generatingPdfId === letter.id}
                            title={letter.signedPdfUrl ? 'Download Signed PDF' : 'Generate PDF'}
                          >
                            {generatingPdfId === letter.id ? <Loader2 size={14} className="el-spin" /> : <Download size={14} />}
                            <span>PDF</span>
                          </button>
                        )}
                        {letter.status === 'draft' && (
                          <button className="el-act-btn el-act-send" onClick={() => handleResendEmail(letter)} title="Mark as Sent">
                            <Send size={14} /> <span>Send</span>
                          </button>
                        )}
                        <button
                          className="el-act-btn el-act-delete"
                          onClick={() => handleDelete(letter.id!)}
                          disabled={deletingId === letter.id}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* PIN row */}
                      <div className="el-item-pin">
                        <Lock size={10} />
                        <span className="el-pin-label">PIN</span>
                        <span className="el-pin-value">{letter.accessPin}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {renderCreateButton(true)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EngagementLettersList;
